import { useReducer, useEffect, ChangeEvent } from 'react';
import { useTheme } from 'styled-components';
import InputTextLine from '../../../../../juristec-ui/core/InputTextLine';
import Select from '../../../../../juristec-ui/core/SelectNew';
import ActionsGroup from '../../../../../juristec-ui/core/ActionsGroup';
import Button from '../../../../../juristec-ui/core/Button';
import Popover from '../../../../../juristec-ui/core/Popover';

import uuidv4 from '../../../../../juristec-ui/utils/functions/randomUUID';
import useToggleState from '../../../../../juristec-ui/hooks/useToggleState';
import ChartIcon from '../../ChartIcon';
import {
  InputWrapper, MainContainer, KpiButton, KpiPicker, IconFlipWrapper,
} from './styled';
import { actionData, kpiType, stateData } from '../../types';
import { ExpandMore } from '../../../../../juristec-ui/icons';
import { dateOptions } from '../../../../../options';
import { SelectOption } from '../../../../../types';
import { verifyInput } from '../../../../../juristec-ui/utils/validators/inputTextValidators';
import { trimString } from '../../../../../juristec-ui/utils/functions/formatString';
import TextArea from '../../../../../juristec-ui/core/TextArea';

interface KpisFormProps {
  fileColumns: Map<string, SelectOption>;
  submitKpi: (kpi: kpiType) => void;
  hide: () => void;
  kpi?: kpiType;
}

const getType = (k: string): string => {
  switch (k) {
    case 'float64':
    case 'int64':
      return 'float';
    case 'datetime64[ns]':
      return 'date';
    case 'object':
    case 'category':
      return 'abc';
    default:
      return k;
  }
};

const filterColumns = (
  columns: SelectOption[], filterType: string | undefined, ...selected: (SelectOption | null)[]
): SelectOption[] => {
  let filtered = [...columns];
  filtered = filtered.filter((c) => ((
    !filterType || (filterType === 'float' ? getType(c.type) === 'float' : getType(c.type) !== 'float')
  ) && (
    getType(c.type) === 'date' || !selected.find((s) => c.value === s?.value)
  )));
  return filtered;
};

const kpisOpts = new Map([
  [
    'Table',
    {
      label: 'Tabela', value: 'Table', singleValue: true, multiValue: true, extraColumn: true,
    },
  ],
  [
    'Value',
    {
      label: 'Valor', value: 'Value', singleValue: true, multiValue: true, extraColumn: true,
    },
  ],
  [
    'Bar',
    {
      label: 'Barras', value: 'Bar', singleValue: false, multiValue: true, extraColumn: true,
    },
  ],
  [
    'Line',
    {
      label: 'Linha', value: 'Line', singleValue: false, multiValue: true, extraColumn: true,
    },
  ],
  [
    'Radar',
    {
      label: 'Radar', value: 'Radar', singleValue: false, multiValue: true, extraColumn: true,
    },
  ],
  [
    'Pie',
    {
      label: 'Pizza', value: 'Pie', singleValue: false, multiValue: false, extraColumn: false,
    },
  ],
  [
    'Funnel',
    {
      label: 'Funil', value: 'Funnel', singleValue: false, multiValue: false, extraColumn: false,
    },
  ],
  [
    'Waffle',
    {
      label: 'Waffle', value: 'Waffle', singleValue: false, multiValue: false, extraColumn: false,
    },
  ],
]);

const valueFiltersOpts = new Map([
  [
    'most',
    { label: 'N Maiores (sem empate)', value: 'most', id: 'most' },
  ],
  [
    'most_inc',
    { value: 'most_inc', label: 'N Maiores (com empate)', id: 'most_inc' },
  ],
  [
    'least',
    { value: 'least', label: 'N Menores (sem empate)', id: 'least' },
  ],
  [
    'least_inc',
    { value: 'least_inc', label: 'N Menores (com empate)', id: 'least_inc' },
  ],
]);

const categoryFuncOpts = [
  { label: 'Contagem', value: 'count', id: 'count' },
  { label: 'Contagem únicos', value: 'nunique', id: 'nunique' },
];
const numericFuncOpts = [
  { label: 'Soma', value: 'sum', id: 'sum' },
  { label: 'Media', value: 'mean', id: 'mean' },
];

const initialState: stateData = {
  name: { value: '', error: true, errorMsg: '' },
  type: kpisOpts.get('Table')!,
  values: [],
  valueOpts: [],
  valueFunc: null,
  valueFuncOpts: [],
  line: null,
  lineOpts: [],
  lineFormat: null,
  lineFormatOpts: [],
  column: null,
  columnOpts: [],
  columnFormat: null,
  columnFormatOpts: [],
  valueFilterType: null,
  valueFilterQty: '5',
  comment: '',
};

const T = {
  SIMPLE_CHANGE: 0,
  VALUE_CHANGE: 1,
  LINE_CHANGE: 2,
  COLUMN_CHANGE: 3,
  LINE_FORMAT_CHANGE: 4,
  COLUMN_FORMAT_CHANGE: 5,
  RESET_COLUMNS: 6,
  SET_TO_EDIT: 7,
} as const;

const reduce = (
  state: stateData, action: actionData,
): stateData => {
  const { type, data } = action;
  switch (type) {
    case T.SIMPLE_CHANGE: {
      return { ...state, ...data };
    }
    case T.VALUE_CHANGE: {
      const fCols = [...data.fileColumns.values()];
      if (!Array.isArray(data.values)) return state;
      let funcs: SelectOption[] = [];
      const first = data.values[0];
      if (data.values.length > 0) {
        if (getType(first.type) === 'float') {
          funcs = numericFuncOpts;
        } else {
          funcs = categoryFuncOpts;
        }
      }
      return {
        ...state,
        values: data.values,
        valueFuncOpts: funcs,
        valueFunc: funcs[0] ?? null,
        valueOpts: filterColumns(
          fCols, getType(first?.type),
          state.line, state.column,
        ),
        lineOpts: filterColumns(
          fCols, undefined,
          ...data.values, state.column,
        ),
        columnOpts: filterColumns(
          fCols, undefined,
          ...data.values, state.line,
        ),
      };
    }
    case T.LINE_CHANGE: {
      const fCols = [...data.fileColumns.values()];
      const dateFmtOpts = dateOptions.filter((d) => d.value !== state.columnFormat?.value);
      return {
        ...state,
        line: data.line ?? null,
        lineFormat: getType(data.line?.type) === 'date' ? dateFmtOpts[0] : null,
        lineFormatOpts: dateFmtOpts,
        columnFormatOpts: dateFmtOpts.slice(1, dateFmtOpts.length),
        valueOpts: filterColumns(
          fCols, getType(state.values[0]?.type),
          data.line, state.column,
        ),
        columnOpts: filterColumns(
          fCols, undefined,
          ...state.values, data.line,
        ),
      };
    }
    case T.COLUMN_CHANGE: {
      const fCols = [...data.fileColumns.values()];
      const dateFmtOpts = dateOptions.filter((d) => d.value !== state.lineFormat?.value);
      return {
        ...state,
        column: data.column ?? null,
        columnFormat: getType(data.column?.type) === 'date' ? dateFmtOpts[0] : null,
        columnFormatOpts: dateFmtOpts,
        lineFormatOpts: dateFmtOpts.slice(1, dateFmtOpts.length),
        valueOpts: filterColumns(
          fCols, getType(state.values[0]?.type),
          data.column, state.line,
        ),
        lineOpts: filterColumns(
          fCols, undefined,
          ...state.values, data.column,
        ),
      };
    }
    case T.LINE_FORMAT_CHANGE: {
      const dateFmtOpts = dateOptions.filter((d) => d.value !== data.lineFormat?.value);
      return {
        ...state,
        lineFormat: data.lineFormat,
        columnFormatOpts: dateFmtOpts,
      };
    }
    case T.COLUMN_FORMAT_CHANGE: {
      const dateFmtOpts = dateOptions.filter((d) => d.value !== data.columnFormat?.value);
      return {
        ...state,
        columnFormat: data.columnFormat,
        lineFormatOpts: dateFmtOpts,
      };
    }
    case T.RESET_COLUMNS: {
      const fCols = [...data.fileColumns.values()];
      return {
        ...state,
        values: [],
        valueOpts: fCols,
        valueFunc: null,
        valueFuncOpts: [],
        line: null,
        lineOpts: fCols,
        lineFormat: null,
        lineFormatOpts: dateOptions,
        column: null,
        columnOpts: fCols,
        columnFormat: null,
        columnFormatOpts: dateOptions,
      };
    }
    case T.SET_TO_EDIT: {
      const tmp: stateData = {
        ...state,
        type: kpisOpts.get(data.type)!,
        name: { value: data.name, error: false, errorMsg: '' },
        values: data.values.map((v: Record<string, string>) => data.fileColumns.get(v.column)),
        line: data.fileColumns.get(data.lines?.[0]?.column) ?? null,
        column: data.fileColumns.get(data.columns?.[0]?.column) ?? null,
        comment: data.comment ?? '',
      };
      const first = data.values[0];
      if (data.values.length > 0) {
        if (getType(first.type) === 'float') {
          tmp.valueFuncOpts = numericFuncOpts;
        } else {
          tmp.valueFuncOpts = categoryFuncOpts;
        }
        tmp.valueFunc = tmp.valueFuncOpts.find((f) => f.value === first.map) ?? null;
      }
      const fCols = [...data.fileColumns.values()];
      tmp.columnFormatOpts = dateOptions.filter((d) => d.value !== data.lines?.[0]?.map);
      tmp.columnFormat = tmp.columnFormatOpts.find(
        (d) => d.value === data.columns?.[0]?.map,
      ) ?? null;
      tmp.lineFormatOpts = dateOptions.filter((d) => d.value !== data.columns?.[0]?.map);
      tmp.lineFormat = tmp.lineFormatOpts.find(
        (d) => d.value === data.lines?.[0]?.map,
      ) ?? null;
      tmp.valueOpts = filterColumns(
        fCols, getType(first?.type), tmp.line, tmp.column,
      );
      tmp.lineOpts = filterColumns(
        fCols, undefined, ...tmp.values, tmp.column,
      );
      tmp.columnOpts = filterColumns(
        fCols, undefined, ...tmp.values, tmp.line,
      );
      if (data.filterValues.length > 0) {
        tmp.valueFilterType = valueFiltersOpts.get(data.filterValues[0].ftype) ?? null;
        tmp.valueFilterQty = (data.filterValues[0].values[0] ?? 5).toString();
      }
      return tmp;
    }
    default: {
      return state;
    }
  }
};

const KpiForm = ({
  fileColumns, submitKpi, hide, kpi,
}: KpisFormProps) => {
  const theme = useTheme();
  const [kpiData, setKpiData] = useReducer(reduce, initialState);
  const [
    typePop, toggleTypePop, closeTypePop,
  ] = useToggleState<boolean>(false);

  useEffect(() => {
    if (kpi) {
      setKpiData({
        type: T.SET_TO_EDIT,
        data: {
          ...kpi,
          fileColumns,
        },
      });
    } else {
      setKpiData({
        type: T.RESET_COLUMNS,
        data: { fileColumns },
      });
    }
  }, [fileColumns, kpi]);

  const handleKpiType = (selected: SelectOption) => {
    setKpiData({
      type: T.SIMPLE_CHANGE,
      data: { type: selected },
    });
  };

  const handleValueChange = (selected: SelectOption[]) => {
    setKpiData({
      type: T.VALUE_CHANGE,
      data: { values: selected, fileColumns },
    });
  };

  const handleValueFuncChange = (selected: SelectOption) => {
    setKpiData({
      type: T.SIMPLE_CHANGE,
      data: { valueFunc: selected },
    });
  };

  const handleLineChange = (selected: SelectOption) => {
    setKpiData({
      type: T.LINE_CHANGE,
      data: { line: selected.value ? selected : null, fileColumns },
    });
  };

  const handleColumnChange = (selected: SelectOption) => {
    setKpiData({
      type: T.COLUMN_CHANGE,
      data: { column: selected.value ? selected : null, fileColumns },
    });
  };

  const handleLineFormatChange = (selected: SelectOption) => {
    setKpiData({
      type: T.LINE_FORMAT_CHANGE,
      data: { lineFormat: selected },
    });
  };

  const handleColumnFormatChange = (selected: SelectOption) => {
    setKpiData({
      type: T.COLUMN_FORMAT_CHANGE,
      data: { columnFormat: selected },
    });
  };

  const handleValueFilterTypeChange = (selected: SelectOption) => {
    setKpiData({
      type: T.SIMPLE_CHANGE,
      data: { valueFilterType: selected },
    });
  };

  const handleValueFilterQty = (
    value: string, min: number | null = null, max: number | null = null,
  ) => {
    let [val] = value.match(/^[0-9]*/) ?? [''];
    if (max && Number(val) > max) val = max.toString();
    else if (min && Number(val) < min) val = min.toString();
    setKpiData({
      type: T.SIMPLE_CHANGE,
      data: { valueFilterQty: val },
    });
  };
  const valueFilterQtyBlur = (e: ChangeEvent<HTMLInputElement>) => (
    handleValueFilterQty(e.target.value, 1, 100)
  );
  const valueFilterQtyChange = (e: ChangeEvent<HTMLInputElement>) => (
    handleValueFilterQty(e.target.value)
  );

  const handleKpiName = (val: string) => {
    const msg = verifyInput(val, true, 2, 40);
    setKpiData({
      type: T.SIMPLE_CHANGE,
      data: {
        name: { value: val, error: msg.length !== 0, errorMsg: msg },
      },
    });
  };
  const kpiNameBlur = (e: ChangeEvent<HTMLInputElement>) => (
    handleKpiName(trimString(e.target.value))
  );
  const kpiNameChange = (e: ChangeEvent<HTMLInputElement>) => handleKpiName(e.target.value);

  const handleComment = (val: string) => {
    setKpiData({
      type: T.SIMPLE_CHANGE,
      data: { comment: val },
    });
  };

  const handleCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => (
    handleComment(e.target.value)
  );

  const handleBlurComment = (e: ChangeEvent<HTMLTextAreaElement>) => {
    handleComment(trimString(e.target.value));
  };

  const validateKpiData = () => (
    kpiData.name.value.length > 0
    && !kpiData.name.error
    && kpiData.values.length > 0
    && kpiData.valueFunc !== null
    && ((
      kpiData.type.multiValue && kpiData.values.length > 1 && kpiData.line !== null
    ) || (
      kpiData.type.singleValue ? kpiData.values.length === 1 : kpiData.line !== null
    ))
  );

  const addKpi = () => {
    if (!validateKpiData()) return;
    const data: kpiType = {
      id: kpi ? kpi.id : uuidv4(),
      type: kpiData.type.value,
      name: kpiData.name.value,
      values: kpiData.values.map((v) => ({
        column: v.value,
        map: kpiData.valueFunc!.value,
        type: v.type as string,
      })),
      lines: [],
      columns: [],
      filterValues: [],
      comment: kpiData.comment,
      author: 'user',
    };
    if (kpiData.line !== null && (kpiData.type.multiValue || !kpiData.type.singleValue)) {
      data.lines.push({
        column: kpiData.line!.value,
        map: getType(kpiData.line!.type) === 'date' ? kpiData.lineFormat!.value : '',
        type: kpiData.line!.type,
      });
      if (kpiData.type.extraColumn && kpiData.values.length === 1 && kpiData.column !== null) {
        data.columns.push({
          column: kpiData.column!.value,
          map: getType(kpiData.column!.type) === 'date' ? kpiData.columnFormat!.value : '',
          type: kpiData.column!.type,
        });
      }
    }
    if (kpiData.valueFilterType !== null && kpiData.type.value !== 'Value') {
      const vFilterCommon = {
        ftype: kpiData.valueFilterType.value,
        values: [Number(kpiData.valueFilterQty)],
        inverse: false,
      };
      if (kpiData.line !== null || kpiData.values.length > 1) {
        data.filterValues.push({
          ...vFilterCommon,
          column: kpiData.line!.value,
          variable: 'line',
        });
      }
      if (!kpiData.type.extraColumn && kpiData.column !== null && kpiData.values.length === 1) {
        data.filterValues.push({
          ...vFilterCommon,
          column: kpiData.column!.value,
          variable: 'column',
        });
      }
    }
    submitKpi(data);
    hide();
  };

  return (
    <MainContainer>
      <InputWrapper>
        <InputTextLine
          label="Nome"
          value={kpiData.name.value}
          error={kpiData.name.errorMsg.length > 0}
          errorMessage={kpiData.name.errorMsg}
          onChange={kpiNameChange}
          setValue={handleKpiName}
          onBlur={kpiNameBlur}
        />
        <Popover
          offset={[0, 0]}
          open={typePop}
          closePopover={closeTypePop}
          style={{
            borderRadius: '8px',
            // @ts-expect-error not typed yet
            backgroundColor: theme.popoverBackground,
            // @ts-expect-error not typed yet
            borderColor: theme.grey,
            border: '1px solid',
            zIndex: '1050',
            overflow: 'hidden',
          }}
        >
          <Popover.Action>
            <KpiButton
              onClick={toggleTypePop}
              className={typePop ? 'kpi-selected' : ''}
            >
              <ChartIcon type={kpiData.type.value} />
              <IconFlipWrapper>
                <ExpandMore />
              </IconFlipWrapper>
            </KpiButton>
          </Popover.Action>
          {/* @ts-expect-error not typed yet */}
          <Popover.Content>
            <KpiPicker>
              {[...kpisOpts].map(([key, val]) => (
                <KpiButton
                  key={key}
                  className={kpiData.type.value === val.value ? 'kpi-selected' : ''}
                  onClick={() => handleKpiType(val)}
                >
                  <ChartIcon type={val.value} />
                </KpiButton>
              ))}
            </KpiPicker>
          </Popover.Content>
        </Popover>
      </InputWrapper>
      <InputWrapper>
        <Select
          label="Valor*"
          // @ts-expect-error not typed yet
          value={kpiData.values}
          options={kpiData.valueOpts}
          onChange={handleValueChange}
          atModal
          searchable
          multiple
          clearable
          sortBy="ascending"
        />
        {kpiData.values.length > 0 && (
          <Select
            label="Função"
            // @ts-expect-error not typed yet
            value={kpiData.valueFunc}
            options={kpiData.valueFuncOpts}
            onChange={handleValueFuncChange}
            atModal
            searchable
          />
        )}
      </InputWrapper>
      {kpiData.values.length > 0 && (
        <InputWrapper>
          <Select
            label="Linha"
            // @ts-expect-error not typed yet
            value={kpiData.line}
            options={kpiData.lineOpts}
            onChange={handleLineChange}
            atModal
            searchable
            clearable
            sortBy="ascending"
          />
          {getType(kpiData.line?.type) === 'date' && (
            <Select
              label="Formato"
              // @ts-expect-error not typed yet
              value={kpiData.lineFormat}
              options={kpiData.lineFormatOpts}
              onChange={handleLineFormatChange}
              atModal
              searchable
            />
          )}
        </InputWrapper>
      )}
      {kpiData.values.length === 1 && kpiData.type.extraColumn && (
        <InputWrapper>
          <Select
            label="Coluna (opcional)"
            // @ts-expect-error not typed yet
            value={kpiData.column}
            options={kpiData.columnOpts}
            onChange={handleColumnChange}
            atModal
            searchable
            clearable
            sortBy="ascending"
          />
          {getType(kpiData.column?.type) === 'date' && (
            <Select
              label="Formato"
              // @ts-expect-error not typed yet
              value={kpiData.columnFormat}
              options={kpiData.columnFormatOpts}
              onChange={handleColumnFormatChange}
              atModal
              searchable
            />
          )}
        </InputWrapper>
      )}
      {kpiData.values.length > 0 && kpiData.type.value !== 'Value' && (
        kpiData.line !== null || kpiData.values.length > 1
      ) && (
        <InputWrapper>
          <Select
            label="Filtro de valores"
            // @ts-expect-error not typed yet
            value={kpiData.valueFilterType}
            options={[...valueFiltersOpts.values()]}
            onChange={handleValueFilterTypeChange}
            atModal
            searchable
            clearable
          />
          {kpiData.valueFilterType !== null && (
            <InputTextLine
              label="Valor de N"
              value={kpiData.valueFilterQty}
              onChange={valueFilterQtyChange}
              setValue={handleValueFilterQty}
              onBlur={valueFilterQtyBlur}
            />
          )}
        </InputWrapper>
      )}
      <InputWrapper>
        <TextArea
          // @ts-expect-error not typed yet
          label="Comentário"
          value={kpiData.comment}
          onChange={handleCommentChange}
          onBlur={handleBlurComment}
        />
      </InputWrapper>
      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={hide}
          variant="outlined"
          size="large"
        >
          Cancelar
        </Button>
        <Button
          style={{ margin: '5px' }}
          size="large"
          onClick={addKpi}
          disabled={!validateKpiData()}
        >
          {kpi ? 'Salvar' : 'Adicionar'}
        </Button>
      </ActionsGroup>
    </MainContainer>
  );
};

export default KpiForm;
